import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePlatformRole } from "@/hooks/usePlatformRole";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft,
  Loader2,
  RefreshCw,
  Receipt,
  Store,
  LogOut,
  Shield,
  Banknote,
  Users,
  FileText,
  Send,
  Clock,
  CheckCircle,
  TrendingUp,
  UserCog,
  Eye,
  CreditCard,
  Mail
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatINR } from "@/lib/currency";
import { toast } from "sonner";
import { MerchantSubscriptionManager } from "@/components/admin/MerchantSubscriptionManager";
import { WaitlistManager } from "@/components/admin/WaitlistManager";

interface ShakatahInvoice {
  id: string;
  merchant_id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  total_fees: number;
  total_amount: number;
  status: string;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
  merchant_name?: string;
  merchant_domain?: string;
}

interface CancellationFee {
  id: string;
  request_id: string;
  merchant_id: string;
  fee_amount: number;
  merchant_share: number;
  shakatah_share: number;
  status: string;
  created_at: string;
  merchant_name?: string;
}

interface MerchantSummary {
  id: string;
  store_name: string;
  shopify_store_domain: string;
  plan: string;
  subscription_status: string;
  trial_ends_at: string | null;
  trial_started_at: string | null;
  billing_cycle_start: string | null;
  splits_this_month: number;
  pending_fees: number;
  total_shakatah_share: number;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { 
    role, 
    loading: roleLoading, 
    isPlatformUser, 
    isSuperAdmin, 
    isRelationshipManager, 
    isManager,
    assignedMerchantIds 
  } = usePlatformRole();
  
  const [invoices, setInvoices] = useState<ShakatahInvoice[]>([]);
  const [fees, setFees] = useState<CancellationFee[]>([]);
  const [merchants, setMerchants] = useState<MerchantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Role-based permissions
  const canGenerateInvoices = isSuperAdmin;
  const canMarkInvoicesPaid = isSuperAdmin;
  const canViewAllMerchants = isSuperAdmin || isRelationshipManager;
  const canManageMerchants = isSuperAdmin;

  // Role display helper
  const getRoleDisplay = (r: string | null) => {
    switch (r) {
      case 'super_admin': return { label: 'Super Admin', color: 'bg-red-500/10 text-red-600' };
      case 'relationship_manager': return { label: 'Relationship Manager', color: 'bg-purple-500/10 text-purple-600' };
      case 'manager': return { label: 'Manager', color: 'bg-blue-500/10 text-blue-600' };
      default: return { label: 'Unknown', color: 'bg-gray-500/10 text-gray-600' };
    }
  };

  const fetchData = async () => {
    try {
      // Fetch all Shakatah invoices with merchant info
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('shakatah_invoices')
        .select('*, merchants(store_name, shopify_store_domain)')
        .order('created_at', { ascending: false });

      if (!invoicesError && invoicesData) {
        const formattedInvoices = invoicesData.map(inv => ({
          ...inv,
          merchant_name: inv.merchants?.store_name,
          merchant_domain: inv.merchants?.shopify_store_domain,
        }));
        setInvoices(formattedInvoices);
      }

      // Fetch all cancellation fees with merchant info
      const { data: feesData, error: feesError } = await supabase
        .from('cancellation_fees')
        .select('*, merchants(store_name)')
        .order('created_at', { ascending: false });

      if (!feesError && feesData) {
        const formattedFees = feesData.map(fee => ({
          ...fee,
          merchant_name: fee.merchants?.store_name,
        }));
        setFees(formattedFees);
      }

      // Fetch merchants with aggregated fee data
      const { data: merchantsData, error: merchantsError } = await supabase
        .from('merchants')
        .select('*');

      if (!merchantsError && merchantsData && feesData) {
        const merchantSummaries = merchantsData.map(m => {
          const merchantFees = feesData.filter(f => f.merchant_id === m.id);
          const pendingFees = merchantFees.filter(f => f.status === 'pending');
          return {
            ...m,
            pending_fees: pendingFees.length,
            total_shakatah_share: pendingFees.reduce((sum, f) => sum + Number(f.shakatah_share), 0),
          };
        });
        setMerchants(merchantSummaries);
      }

    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!roleLoading && isPlatformUser) {
      fetchData();
    } else if (!roleLoading) {
      setLoading(false);
    }
  }, [roleLoading, isPlatformUser]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    toast.success("Signed out successfully");
  };

  const generateAllInvoices = async () => {
    setGeneratingInvoices(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-shakatah-invoice`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}), // Empty = generate for all merchants
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to generate invoices");
      }

      if (result.invoices_generated > 0) {
        toast.success(`Generated ${result.invoices_generated} invoice(s) successfully!`);
        await fetchData();
      } else {
        toast.info("No pending fees to invoice");
      }
    } catch (error: any) {
      console.error("Error generating invoices:", error);
      toast.error(error.message || "Failed to generate invoices");
    } finally {
      setGeneratingInvoices(false);
    }
  };

  const generateMerchantInvoice = async (merchantId: string, merchantName: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-shakatah-invoice`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ merchant_id: merchantId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to generate invoice");
      }

      if (result.invoices_generated > 0) {
        toast.success(`Invoice sent to ${merchantName}`);
        await fetchData();
      } else {
        toast.info(`No pending fees for ${merchantName}`);
      }
    } catch (error: any) {
      console.error("Error generating invoice:", error);
      toast.error(error.message || "Failed to generate invoice");
    }
  };

  const markInvoicePaid = async (invoiceId: string) => {
    try {
      const { error } = await supabase
        .from('shakatah_invoices')
        .update({ status: 'paid', paid_at: new Date().toISOString() })
        .eq('id', invoiceId);

      if (error) throw error;

      toast.success("Invoice marked as paid");
      await fetchData();
    } catch (error: any) {
      console.error("Error marking invoice paid:", error);
      toast.error("Failed to update invoice");
    }
  };

  // Stats
  const stats = {
    totalInvoices: invoices.length,
    totalRevenue: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + Number(i.total_amount), 0),
    pendingRevenue: invoices.filter(i => i.status === 'sent').reduce((sum, i) => sum + Number(i.total_amount), 0),
    pendingFees: fees.filter(f => f.status === 'pending').length,
    totalMerchants: merchants.length,
    merchantsWithPendingFees: merchants.filter(m => m.pending_fees > 0).length,
  };

  if (loading || roleLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isPlatformUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have permission to access the admin dashboard.
            </p>
            <Button onClick={() => navigate("/")} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card sticky top-0 z-10">
        <div className="container max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-semibold">Shakatah Admin</h1>
                  <Badge className={getRoleDisplay(role).color}>
                    {getRoleDisplay(role).label}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2">
              <Receipt className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="merchants" className="gap-2">
              <Store className="h-4 w-4" />
              Merchants
            </TabsTrigger>
            <TabsTrigger value="fees" className="gap-2">
              <Banknote className="h-4 w-4" />
              All Fees
            </TabsTrigger>
            <TabsTrigger value="waitlist" className="gap-2">
              <Mail className="h-4 w-4" />
              Waitlist
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Revenue</p>
                      <p className="text-lg font-bold text-green-600">{formatINR(stats.totalRevenue)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-500/10 rounded-lg">
                      <Clock className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pending Revenue</p>
                      <p className="text-lg font-bold text-yellow-600">{formatINR(stats.pendingRevenue)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Receipt className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Invoices</p>
                      <p className="text-2xl font-bold">{stats.totalInvoices}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-500/10 rounded-lg">
                      <Banknote className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pending Fees</p>
                      <p className="text-2xl font-bold text-orange-600">{stats.pendingFees}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Store className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Merchants</p>
                      <p className="text-2xl font-bold">{stats.totalMerchants}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">To Invoice</p>
                      <p className="text-2xl font-bold text-purple-600">{stats.merchantsWithPendingFees}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions - Super Admin only */}
            {canGenerateInvoices ? (
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>Generate and send invoices to merchants</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    <Button
                      onClick={generateAllInvoices}
                      disabled={generatingInvoices || stats.pendingFees === 0}
                      className="bg-primary"
                    >
                      {generatingInvoices ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Generate All Invoices ({stats.merchantsWithPendingFees} merchants)
                        </>
                      )}
                    </Button>
                  </div>
                  {stats.pendingFees === 0 && (
                    <p className="text-sm text-muted-foreground mt-3">
                      No pending fees to invoice at this time.
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Eye className="h-5 w-5" />
                    <div>
                      <p className="font-medium">View-Only Mode</p>
                      <p className="text-sm">
                        {isManager 
                          ? "As a Manager, you can view data but cannot generate invoices." 
                          : "As a Relationship Manager, you can only manage your assigned merchants."}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Invoices */}
            {invoices.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {invoices.slice(0, 5).map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            invoice.status === 'paid' ? 'bg-green-500/10' : 'bg-blue-500/10'
                          }`}>
                            <Receipt className={`h-4 w-4 ${
                              invoice.status === 'paid' ? 'text-green-600' : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-mono font-semibold text-sm">{invoice.invoice_number}</p>
                            <p className="text-xs text-muted-foreground">{invoice.merchant_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold">{formatINR(invoice.total_amount)}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(invoice.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge className={
                            invoice.status === 'paid' ? 'bg-green-500/10 text-green-600' : 
                            'bg-blue-500/10 text-blue-600'
                          }>
                            {invoice.status === 'paid' ? 'Paid' : 'Sent'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Merchant Subscriptions
                </CardTitle>
                <CardDescription>
                  Manage merchant plans and subscription status. Update manually after receiving payment.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MerchantSubscriptionManager 
                  merchants={merchants} 
                  onRefresh={fetchData} 
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  All Invoices
                </CardTitle>
                <CardDescription>Invoices sent to merchants for Shakatah's cancellation fee share</CardDescription>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No invoices generated yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between p-4 rounded-lg border">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${
                            invoice.status === 'paid' ? 'bg-green-500/10' : 'bg-blue-500/10'
                          }`}>
                            <Receipt className={`h-5 w-5 ${
                              invoice.status === 'paid' ? 'text-green-600' : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-mono font-semibold">{invoice.invoice_number}</p>
                            <p className="text-sm text-muted-foreground">
                              {invoice.merchant_name} • {invoice.merchant_domain}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Period: {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold text-lg">{formatINR(invoice.total_amount)}</p>
                            <p className="text-xs text-muted-foreground">{invoice.total_fees} fee(s)</p>
                          </div>
                          <Badge className={
                            invoice.status === 'paid' ? 'bg-green-500/10 text-green-600' : 
                            'bg-blue-500/10 text-blue-600'
                          }>
                            {invoice.status === 'paid' ? 'Paid' : 'Sent'}
                          </Badge>
                          {invoice.status === 'sent' && canMarkInvoicesPaid && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => markInvoicePaid(invoice.id)}
                              className="border-green-500/30 text-green-600 hover:bg-green-500/10"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Merchants Tab */}
          <TabsContent value="merchants" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="h-5 w-5" />
                  Merchants
                </CardTitle>
                <CardDescription>All registered merchants and their pending fees</CardDescription>
              </CardHeader>
              <CardContent>
                {merchants.length === 0 ? (
                  <div className="text-center py-12">
                    <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No merchants registered yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {merchants.map((merchant) => (
                      <div key={merchant.id} className="flex items-center justify-between p-4 rounded-lg border">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Store className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold">{merchant.store_name}</p>
                            <p className="text-sm text-muted-foreground">{merchant.shopify_store_domain}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold">
                              {merchant.pending_fees > 0 ? (
                                <span className="text-orange-600">{merchant.pending_fees} pending</span>
                              ) : (
                                <span className="text-green-600">No pending fees</span>
                              )}
                            </p>
                            {merchant.pending_fees > 0 && (
                              <p className="text-sm text-muted-foreground">
                                {formatINR(merchant.total_shakatah_share)} to invoice
                              </p>
                            )}
                          </div>
                          {merchant.pending_fees > 0 && canGenerateInvoices && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => generateMerchantInvoice(merchant.id, merchant.store_name)}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Invoice
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Fees Tab */}
          <TabsContent value="fees" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Banknote className="h-5 w-5" />
                  All Cancellation Fees
                </CardTitle>
                <CardDescription>Complete log of all cancellation fees across merchants</CardDescription>
              </CardHeader>
              <CardContent>
                {fees.length === 0 ? (
                  <div className="text-center py-12">
                    <Banknote className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No cancellation fees recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {fees.map((fee) => (
                      <div key={fee.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            fee.status === 'paid' ? 'bg-green-500/10' : 
                            fee.status === 'invoiced' ? 'bg-blue-500/10' :
                            'bg-yellow-500/10'
                          }`}>
                            <Banknote className={`h-4 w-4 ${
                              fee.status === 'paid' ? 'text-green-600' : 
                              fee.status === 'invoiced' ? 'text-blue-600' :
                              'text-yellow-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{fee.merchant_name || 'Unknown Merchant'}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(fee.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-primary">{formatINR(fee.shakatah_share)}</p>
                            <p className="text-xs text-muted-foreground">
                              of {formatINR(fee.fee_amount)} total
                            </p>
                          </div>
                          <Badge className={
                            fee.status === 'paid' ? 'bg-green-500/10 text-green-600' : 
                            fee.status === 'invoiced' ? 'bg-blue-500/10 text-blue-600' :
                            'bg-yellow-500/10 text-yellow-600'
                          }>
                            {fee.status === 'paid' ? 'Paid' : 
                             fee.status === 'invoiced' ? 'Invoiced' : 'Pending'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <WaitlistManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
